import { Cart, CartLineNode, ShopifyCart, ShopifyCartItem } from './shopify';
import { getCart } from './cart.browser';
import { convertStringAmountToCents } from '@/utils/currency';

export type FetchShopifyCartResult =
  | { ok: true; data: ShopifyCart; currency: string }
  | { ok: false; errorMessage: string };

/**
 * Extracts the numeric variant ID from a Shopify GID
 * @param gid - The Shopify Global ID (e.g., "gid://shopify/ProductVariant/12345")
 * @returns The numeric ID as a number
 */
function extractNumericId(gid: string): number {
  const match = gid.match(/\/(\d+)$/);
  return match ? parseInt(match[1], 10) : 0;
}

/**
 * Converts GraphQL cart line attributes to AJAX properties format
 * @param attributes - Array of { key, value } attributes
 * @returns Record of properties
 */
function attributesToProperties(
  attributes: Array<{ key: string; value: string }>,
): Record<string, string | null> {
  return attributes.reduce(
    (acc, { key, value }) => {
      acc[key] = value;
      return acc;
    },
    {} as Record<string, string | null>,
  );
}

/**
 * Converts a GraphQL CartLineNode to AJAX ShopifyCartItem format
 * @param node - The cart line node from GraphQL
 * @param index - The index of the item in the cart
 * @returns ShopifyCartItem in AJAX format
 */
function cartLineNodeToShopifyCartItem(
  node: CartLineNode,
  index: number,
): ShopifyCartItem {
  const merchandise = node.merchandise;
  const product = merchandise.product;
  const priceAmount = merchandise.price?.amount ?? '0';
  const priceCents = convertStringAmountToCents(String(priceAmount));
  const subtotalAmount = node.cost?.subtotalAmount?.amount ?? '0';
  const subtotalCents = convertStringAmountToCents(String(subtotalAmount));

  return {
    id: index + 1, // AJAX uses sequential IDs
    properties: attributesToProperties(node.attributes || []),
    quantity: node.quantity,
    variant_id: extractNumericId(merchandise.id),
    key: `${extractNumericId(merchandise.id)}:${node.id}`,
    title: product?.title || '',
    price: priceCents,
    original_price: priceCents,
    discounted_price: priceCents,
    line_price: subtotalCents,
    original_line_price: subtotalCents,
    total_discount: 0,
    discounts: [],
    sku: merchandise.sku || '',
    grams: 0,
    vendor: 'Lovevery',
    taxable: true,
    product_id: product ? extractNumericId(product.id) : 0,
    product_type: product?.productType || '',
    gift_card: false,
    url: product?.handle ? `/products/${product.handle}` : '',
    featured_image: merchandise.image
      ? {
          src: merchandise.image.url,
          alt: merchandise.image.altText,
        }
      : undefined,
    handle: product?.handle || '',
    requires_shipping: true,
    product_title: product?.title || '',
    product_description: '',
    variant_title: merchandise.title || undefined,
    variant_options: merchandise.title ? [merchandise.title] : [],
    options_with_values: merchandise.title
      ? [{ name: 'Title', value: merchandise.title }]
      : [],
  };
}

/**
 * Extracts cart token from a Shopify cart GID
 * @param cartId - The cart GID (e.g., "gid://shopify/Cart/c1-xxx")
 * @returns The cart token (e.g., "c1-xxx")
 */
function extractCartToken(cartId: string): string {
  return cartId.replace('gid://shopify/Cart/', '');
}

/**
 * Converts a GraphQL Cart to AJAX ShopifyCart format
 * This adapter allows using the Storefront GraphQL API response
 * with code that expects the AJAX /cart.js format
 *
 * @param cart - The cart from Storefront GraphQL API
 * @param currency - The currency code (defaults to 'USD')
 * @returns ShopifyCart in AJAX format
 */
export function graphqlCartToShopifyCart(
  cart: Cart,
  currency = 'USD',
): ShopifyCart {
  const items = cart.lines.edges.map(({ node }, index) =>
    cartLineNodeToShopifyCartItem(node, index),
  );

  // Use totalAmount from Shopify API directly instead of calculating
  const totalAmount = cart.cost?.totalAmount?.amount;
  const totalPrice = convertStringAmountToCents(String(totalAmount ?? '0'));

  // Use totalQuantity from Shopify API directly
  const itemCount = cart.totalQuantity;

  return {
    token: extractCartToken(cart.id),
    note: null,
    attributes: {},
    original_total_price: totalPrice,
    total_price: totalPrice,
    total_discount: 0,
    total_weight: 0,
    item_count: itemCount,
    items,
    requires_shipping: items.some((item) => item.requires_shipping),
    currency,
    items_subtotal_price: totalPrice,
    cart_level_discount_applications: [],
  };
}

/**
 * Type guard to check if the cart response is successful
 */
export function isCartResponseSuccess(response: {
  ok: boolean;
  data?: Cart;
  errorMessage?: string;
}): response is { ok: true; data: Cart } {
  return response.ok && 'data' in response && response.data !== undefined;
}

/**
 * Extracts the currency code from a GraphQL cart response
 * @param cart - The cart from Storefront GraphQL API
 * @returns The currency code (defaults to 'USD')
 */
function extractCurrencyFromCart(cart: Cart): string {
  // Prefer cart-level currency, fall back to first item's currency
  if (cart.cost?.totalAmount?.currencyCode) {
    return cart.cost.totalAmount.currencyCode;
  }

  const firstItem = cart.lines.edges[0]?.node;
  return (
    firstItem?.merchandise.price?.currencyCode ||
    firstItem?.cost?.subtotalAmount?.currencyCode ||
    'USD'
  );
}

/**
 * Fetches the cart using GraphQL API and converts it to ShopifyCart format
 * This is a convenience function that combines getCart, isCartResponseSuccess,
 * and graphqlCartToShopifyCart into a single call.
 *
 * @returns FetchShopifyCartResult with either the converted cart or an error
 */
export async function fetchAndConvertCart(): Promise<FetchShopifyCartResult> {
  const cartResponse = await getCart();

  if (!isCartResponseSuccess(cartResponse)) {
    const errorMessage =
      'errorMessage' in cartResponse
        ? cartResponse.errorMessage
        : 'Unknown error';
    console.error('Failed to get cart:', errorMessage);
    return { ok: false, errorMessage: errorMessage || 'Failed to get cart' };
  }

  const currency = extractCurrencyFromCart(cartResponse.data);
  const shopifyCart = graphqlCartToShopifyCart(cartResponse.data, currency);

  return { ok: true, data: shopifyCart, currency };
}
